import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "searchInput", "results", "selectedList", "submitButton", "selectedIds", "loading"]
  static values = {
    context: String,      // 'club' or 'team'
    contextId: Number,
    excludedIds: { type: Array, default: [] }
  }

  connect() {
    this.selectedProducts = new Map()
    this.searchTimeout = null
  }

  open(event) {
    event.preventDefault()
    this.modalTarget.classList.remove("hidden")
    this.searchInputTarget.value = ""
    this.resultsTarget.innerHTML = ""
    this.selectedProducts.clear()
    this.updateSelectedList()
    this.updateSubmitButton()
    
    // Focus search input
    setTimeout(() => this.searchInputTarget.focus(), 100)
    
    // Load initial products
    this.search()
  }

  close(event) {
    if (event) event.preventDefault()
    this.modalTarget.classList.add("hidden")
  }

  closeOnEscape(event) {
    if (event.key === "Escape" && !this.modalTarget.classList.contains("hidden")) {
      this.close()
    }
  }

  search() {
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => this.performSearch(), 300)
  }

  async performSearch() {
    const query = this.searchInputTarget.value.trim()
    
    // Get currently excluded IDs (already assigned + currently selected)
    const excludedIds = [
      ...this.excludedIdsValue,
      ...Array.from(this.selectedProducts.keys())
    ].join(",")

    this.showLoading()

    try {
      const response = await fetch(
        `/api/products/search?q=${encodeURIComponent(query)}&context=${this.contextValue}&context_id=${this.contextIdValue}&excluded_ids=${excludedIds}`,
        {
          headers: {
            "Accept": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
          }
        }
      )

      if (!response.ok) throw new Error("Search failed")
      
      const data = await response.json()
      
      // Handle error response
      if (data.error) {
        throw new Error(data.error)
      }
      
      this.renderResults(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error("Search error:", error)
      this.resultsTarget.innerHTML = `
        <div class="p-4 text-center text-sm text-red-600">
          Failed to search products. Please try again.
        </div>
      `
    }

    this.hideLoading()
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
    }
  }

  renderResults(products) {
    if (products.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="p-4 text-center text-sm text-gray-500">
          No products found. Try a different search term.
        </div>
      `
      return
    }

    this.resultsTarget.innerHTML = products.map(product => `
      <button type="button"
              class="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition border-b border-gray-100 last:border-b-0"
              data-action="click->product-assignment#selectProduct"
              data-product-id="${product.id}"
              data-product-name="${this.escapeHtml(product.name)}">
        <div class="font-medium text-gray-900">${this.escapeHtml(product.name)}</div>
        ${product.description ? `<div class="text-sm text-gray-500 mt-0.5">${this.escapeHtml(product.description)}</div>` : ''}
      </button>
    `).join("")
  }

  selectProduct(event) {
    const button = event.currentTarget
    const id = parseInt(button.dataset.productId)
    const name = button.dataset.productName

    if (!this.selectedProducts.has(id)) {
      this.selectedProducts.set(id, name)
      this.updateSelectedList()
      this.updateSubmitButton()
      // Re-search to update the list
      this.performSearch()
    }
  }

  removeProduct(event) {
    const id = parseInt(event.currentTarget.dataset.productId)
    this.selectedProducts.delete(id)
    this.updateSelectedList()
    this.updateSubmitButton()
    // Re-search to show the product again
    this.performSearch()
  }

  updateSelectedList() {
    if (this.selectedProducts.size === 0) {
      this.selectedListTarget.innerHTML = `
        <div class="text-sm text-gray-500 italic">No products selected</div>
      `
      return
    }

    this.selectedListTarget.innerHTML = Array.from(this.selectedProducts.entries()).map(([id, name]) => `
      <div class="inline-flex items-center gap-1.5 bg-indigo-100 text-indigo-800 px-3 py-1.5 rounded-full text-sm font-medium">
        <span>${this.escapeHtml(name)}</span>
        <button type="button"
                class="hover:text-indigo-600 focus:outline-none"
                data-action="click->product-assignment#removeProduct"
                data-product-id="${id}">
          <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    `).join("")
  }

  updateSubmitButton() {
    const count = this.selectedProducts.size
    this.submitButtonTarget.disabled = count === 0
    this.submitButtonTarget.textContent = count === 0 
      ? "Select products to assign" 
      : `Assign ${count} product${count === 1 ? '' : 's'}`
  }

  submit(event) {
    event.preventDefault()
    
    if (this.selectedProducts.size === 0) return

    // Set the hidden field with selected IDs
    this.selectedIdsTarget.value = Array.from(this.selectedProducts.keys()).join(",")
    
    // Submit the form
    event.target.closest("form").submit()
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}

